'use client';

import { jsx, jsxs } from "react/jsx-runtime";
import a__default, { forwardRef, useMemo, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { classNames } from "../../utils/styles/classNames.js";
import { FormControl } from "../../atoms/FormControl/FormControl.js";
import { CaretUp, CaretDown } from "../../../react-icons.js";
import { Text } from "../../atoms/Text/Text.js";
import { useId } from "../../utils/hooks/useId.js";
import { Option } from "./Select.Option.js";
import { wrapper, inputLabel, focused, inputBorder, input, listDisplay, listHeight, list } from "./Select.css.js";
import { Loading } from "./Select.Loading.js";
import { Empty } from "./Select.Empty.js";
import { parseProps } from "./utils/parseProps.js";
import { getCurrentState } from "./utils/getCurrentState.js";
import { useFloatingUi } from "./utils/useFloatingUi.js";
import { getLabelId } from "./utils/constants.js";
function SelectComponent(props, ref) {
  var _a;
  const { selectFieldProps, formControlProps } = parseProps(props);
  const {
    options,
    dataQa,
    onChange,
    emptyLabel,
    isLoading,
    placeholder,
    value,
    noOptionsProvided
  } = selectFieldProps;
  const { disabled, error, label, ...restFormControlProps } = formControlProps;
  const autoId = useId();
  const labelId = getLabelId(autoId);
  const currentState = getCurrentState({
    disabled,
    error
  });
  const optionsContent = useMemo(() => options == null ? void 0 : options.map((i) => i.label), [options]);
  const selectedIndex = (options == null ? void 0 : options.findIndex((i) => i.value === value)) ?? -1;
  const [isOpen, setIsOpen] = a__default.useState(false);
  const handleOpenChange = useCallback(
    (o) => {
      if (!disabled) {
        setIsOpen(o);
      }
    },
    [setIsOpen, disabled]
  );
  const handleChange = useCallback(
    (i) => {
      if (options) {
        onChange(options[i].value);
      }
    },
    [onChange, options]
  );
  const {
    dropdownProps,
    labelProps,
    getItemProps,
    triggerProps,
    activeIndex,
    listRef,
    isTypingRef,
    context
  } = useFloatingUi({
    isOpen,
    onOpenChange: handleOpenChange,
    onSelect: handleChange,
    optionsContent,
    selectedIndex,
    triggerRef: ref
  });
  const handleSelect = (index) => {
    handleChange(index);
    setIsOpen(false);
  };
  const selectedOptionLabel = selectedIndex !== -1 && !!options ? (_a = options[selectedIndex]) == null ? void 0 : _a.label : void 0;
  const hasSelectedOption = !!selectedOptionLabel;
  const triggerTextColor = hasSelectedOption ? "onLight.text.primary" : "onLight.text.tertiary";
  const CaretIcon = isOpen ? CaretUp : CaretDown;
  const hasCaption = !!(options == null ? void 0 : options.find((i) => !!i.caption));
  const hasBeenOpen = useRef(false);
  const focusTrigger = useCallback(() => {
    if (context.refs.domReference) {
      const domReference = context.refs.domReference;
      domReference.current.focus();
    }
  }, [context.refs.domReference]);
  useEffect(() => {
    if (isOpen) {
      hasBeenOpen.current = true;
    }
    if (hasBeenOpen.current && !isOpen) focusTrigger();
    const handleKeyDown = (event) => {
      if (event.key === "Tab") {
        event.preventDefault();
        setIsOpen(false);
      }
    };
    if (isOpen && window) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      if (isOpen && window) {
        window.removeEventListener("keydown", handleKeyDown);
      }
    };
  }, [isOpen]);
  return /* @__PURE__ */ jsx(
    FormControl,
    {
      ...restFormControlProps,
      disabled,
      error,
      label: { ...label, ...labelProps, id: labelId },
      a11yAnnounceRequired: true,
      children: /* @__PURE__ */ jsxs("div", { className: wrapper, children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            ...triggerProps,
            "aria-autocomplete": "none",
            "aria-labelledby": labelId,
            className: classNames(
              input,
              inputBorder[currentState],
              isOpen && focused
            ),
            "data-qa": dataQa,
            children: [
              /* @__PURE__ */ jsx(
                Text,
                {
                  "aria-hidden": !hasSelectedOption && placeholder ? "true" : void 0,
                  className: inputLabel,
                  color: disabled ? "onLight.disabled.muted" : triggerTextColor,
                  variant: "body.base",
                  children: selectedOptionLabel || placeholder
                }
              ),
              /* @__PURE__ */ jsx(
                CaretIcon,
                {
                  color: disabled ? "onLight.disabled.muted" : "onLight.icon.secondary",
                  size: "sm"
                }
              )
            ]
          }
        ),
        isOpen && createPortal(
          /* @__PURE__ */ jsxs(
            "ul",
            {
              ...dropdownProps,
              className: classNames(
                list,
                listHeight[hasCaption ? "withCaption" : "default"],
                listDisplay[isOpen ? "default" : "hidden"]
              ),
              children: [
                /* @__PURE__ */ jsx(
                  Empty,
                  {
                    emptyLabel,
                    isLoading: !!isLoading,
                    noOptionsProvided
                  }
                ),
                /* @__PURE__ */ jsx(Loading, { isLoading: !!isLoading }),
                options == null ? void 0 : options.map((option, i) => /* @__PURE__ */ jsx(
                  Option,
                  {
                    isHighlighted: i === activeIndex,
                    isSelected: i === selectedIndex,
                    itemProps: {
                      "aria-selected": i === selectedIndex && i === activeIndex,
                      role: "option",
                      tabIndex: i === activeIndex ? 0 : -1,
                      ref: (node) => {
                        listRef.current[i] = node;
                      },
                      ...getItemProps({
                        // Handle pointer select.
                        onClick() {
                          handleSelect(i);
                        },
                        // Handle keyboard select.
                        onKeyDown(event) {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            handleSelect(i);
                          }
                          if (event.key === " " && !isTypingRef.current) {
                            event.preventDefault();
                            handleSelect(i);
                          }
                        }
                      })
                    },
                    option
                  },
                  option.value
                ))
              ]
            }
          ),
          document.body
        )
      ] })
    }
  );
}
const Select = /* @__PURE__ */ forwardRef(SelectComponent);
export {
  Select
};
